import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { portalColaboradorService } from '../../services/portal-colaborador.service';
import UploadAtestadoModal from '../../components/Saude/UploadAtestadoModal';
import {
  Heart,
  Activity,
  ShieldCheck,
  CheckCircle2,
  Clock,
  PlusCircle,
  Sparkles,
  Info,
  AlertTriangle,
  TrendingUp,
  Stethoscope,
  ShieldAlert,
  FileText,
} from 'lucide-react';
import type { SistemaAnatomico } from '@sstexames/shared-types';

interface Props {
  colaboradorId: number;
  empresaId: number;
  nomeColaborador: string;
}

export default function MinhaSaudeColaboradorTab({
  colaboradorId,
  empresaId,
  nomeColaborador,
}: Props) {
  const getTrajetoriaColaboradorBadge = (trajetoria?: 'MELHORANDO' | 'ESTAVEL' | 'OSCILANTE' | 'AGRAVANDO') => {
    switch (trajetoria) {
      case 'MELHORANDO':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            Evolução: Em Melhora
          </span>
        );
      case 'ESTAVEL':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800 border border-blue-300">
            <ShieldCheck className="w-4 h-4 text-blue-600" />
            Evolução: Estável
          </span>
        );
      case 'OSCILANTE':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-300">
            <Activity className="w-4 h-4 text-amber-600" />
            Evolução: Em Acompanhamento
          </span>
        );
      case 'AGRAVANDO':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-rose-100 text-rose-800 border border-rose-300">
            <AlertTriangle className="w-4 h-4 text-rose-600" />
            Evolução: Requer Atenção
          </span>
        );
      default:
        return null;
    }
  };

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { data: saude, isLoading, refetch } = useQuery({
    queryKey: ['minha-saude-colaborador', colaboradorId],
    queryFn: () => portalColaboradorService.getMinhaSaude(colaboradorId),
    enabled: Boolean(colaboradorId),
  });

  const showToast = (type: 'success' | 'error', text: string) => {
    setToastMessage({ type, text });
    setTimeout(() => setToastMessage(null), 4000);
  };

  const getCorSistema = (sistema: SistemaAnatomico) => {
    switch (sistema) {
      case 'MUSCULOESQUELETICO':
        return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'RESPIRATORIO':
        return 'text-sky-600 bg-sky-50 border-sky-200';
      case 'AUDITIVO':
        return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'CARDIOVASCULAR':
        return 'text-rose-600 bg-rose-50 border-rose-200';
      case 'VISUAL':
        return 'text-indigo-600 bg-indigo-50 border-indigo-200';
      default:
        return 'text-emerald-600 bg-emerald-50 border-emerald-200';
    }
  };

  const recomendacoes = saude?.recomendacoes_individuais;
  const simulador = saude?.simulador_panoramas;

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Toast Alert */}
      {toastMessage && (
        <div
          className={`p-3 rounded-xl text-xs font-semibold flex items-center justify-between border shadow-sm ${
            toastMessage.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
              : 'bg-red-50 text-red-800 border-red-200'
          }`}
        >
          <span>{toastMessage.text}</span>
          <button
            onClick={() => setToastMessage(null)}
            className="text-gray-400 hover:text-gray-600 text-sm ml-2"
          >
            ×
          </button>
        </div>
      )}

      {/* Banner de Boas-vindas Acolhedor */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-indigo-700 rounded-2xl p-6 text-white shadow-lg relative overflow-hidden">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 backdrop-blur-md text-xs font-medium mb-2">
              <Sparkles className="w-3.5 h-3.5 text-amber-300" />
              Portal do Colaborador • Bem-Estar Ocupacional
            </span>
            <h2 className="text-xl md:text-2xl font-bold tracking-tight">
              Olá, {nomeColaborador.split(' ')[0]}! Cuide da sua saúde.
            </h2>
            <p className="text-xs md:text-sm text-emerald-100 mt-1 max-w-xl">
              Aqui você tem visibilidade clara dos seus exames médicos ocupacionais, atestados enviados, simulações preventivas e orientações de bem-estar.
            </p>
          </div>

          <button
            onClick={() => setIsUploadModalOpen(true)}
            className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl bg-white text-emerald-800 font-bold text-xs hover:bg-emerald-50 shadow-md transition-all gap-2 self-start md:self-auto hover:scale-105 cursor-pointer"
          >
            <PlusCircle className="w-4 h-4 text-emerald-600" />
            Enviar Atestado Médico
          </button>
        </div>
      </div>

      {/* Cards de Resumo & Scores Individuais */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Score de Saúde Individual */}
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
            <Heart className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <span className="text-[11px] text-gray-400 font-bold uppercase tracking-wider block">
              Qualidade de Saúde
            </span>
            <div className="flex items-baseline gap-1">
              <span className="text-xl font-extrabold text-emerald-900 font-mono">
                {isLoading ? '---' : saude?.qualidade_saude_score ?? 88}
              </span>
              <span className="text-[10px] text-emerald-700 font-semibold">/100</span>
            </div>
            <span className="text-[10px] font-bold text-emerald-700 uppercase">
              {recomendacoes?.qualidade_geral_saude?.classificacao || 'BOA'}
            </span>
          </div>
        </div>

        {/* Potencial de Acidente */}
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <span className="text-[11px] text-gray-400 font-bold uppercase tracking-wider block">
              Potencial de Acidente
            </span>
            <span className="text-xl font-extrabold text-amber-900 font-mono">
              {isLoading ? '---' : recomendacoes?.potencial_acidente?.score ?? 15}%
            </span>
            <span className="text-[10px] font-bold text-amber-700 block uppercase">
              Grau {recomendacoes?.potencial_acidente?.classificacao || 'BAIXO'}
            </span>
          </div>
        </div>

        {/* Exames Realizados */}
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
            <Activity className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <span className="text-[11px] text-gray-400 font-bold uppercase tracking-wider block">
              Exames Realizados
            </span>
            <span className="text-xl font-extrabold text-gray-900 font-mono">
              {isLoading ? '---' : saude?.total_exames_realizados || 0}
            </span>
            <span className="text-[10px] text-indigo-600 block">
              {saude?.total_atestados_enviados || 0} atestado(s)
            </span>
          </div>
        </div>

        {/* Vigência Ocupacional */}
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <span className="text-[11px] text-gray-400 font-bold uppercase tracking-wider block">
              Vigência PCMSO
            </span>
            <span className="text-xs font-bold text-teal-800 block mt-0.5">
              Apto Vigente
            </span>
            <span className="text-[10px] text-gray-500">
              Próximo: Periódico
            </span>
          </div>
        </div>
      </div>

      {/* Simulador Preditivo de Propensões de Saúde (3 Cenários 12 Meses) */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-gray-100">
          <div>
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-indigo-600" />
              Projeção Preditiva de Saúde (Próximos 12 Meses)
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Simulação de trajetórias de saúde conforme a sua adesão aos cuidados ergonômicos e preventivos.
            </p>
          </div>
          <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
            Inteligência Preditiva
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Cenário Otimista */}
          <div className="p-4 rounded-xl border border-emerald-300 bg-gradient-to-b from-emerald-50/60 to-white space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 uppercase">
                Cenário Otimista
              </span>
              <span className="text-xs font-mono font-bold text-emerald-700">
                {simulador?.otimista.probabilidade_afastamento_12m ?? 3.5}% prob.
              </span>
            </div>
            <h4 className="text-xs font-bold text-emerald-950">
              {simulador?.otimista.descricao || 'Adesão plena às orientações preventivas'}
            </h4>
            <p className="text-[11px] text-gray-600">
              Estimativa de dias ganhos em disposição e produtividade: <strong>+{simulador?.otimista.estimativa_dias_ganhos ?? 8} dias</strong>.
            </p>
            <ul className="text-[11px] text-emerald-900 space-y-1 pt-1 border-t border-emerald-100">
              {(simulador?.otimista.beneficios_esperados || [
                'Estabilização de queixas posturais',
                'Zero afastamentos no período',
              ]).map((b, i) => (
                <li key={i} className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Cenário Realista */}
          <div className="p-4 rounded-xl border border-blue-300 bg-gradient-to-b from-blue-50/60 to-white space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-800 uppercase">
                Cenário Realista
              </span>
              <span className="text-xs font-mono font-bold text-blue-700">
                {simulador?.realista.probabilidade_afastamento_12m ?? 8.2}% prob.
              </span>
            </div>
            <h4 className="text-xs font-bold text-blue-950">
              {simulador?.realista.descricao || 'Manutenção do padrão ocupacional atual'}
            </h4>
            <p className="text-[11px] text-gray-600">
              Dias estimados de desconforto leve no ano: <strong>{simulador?.realista.estimativa_dias_afastamento ?? 2} dias</strong>.
            </p>
            <ul className="text-[11px] text-blue-900 space-y-1 pt-1 border-t border-blue-100">
              {(simulador?.realista.riscos_esperados || [
                'Tensão cervical esporádica',
                'Necessidade de pausas ativas',
              ]).map((r, i) => (
                <li key={i} className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Cenário Pessimista */}
          <div className="p-4 rounded-xl border border-rose-300 bg-gradient-to-b from-rose-50/60 to-white space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-rose-100 text-rose-800 uppercase">
                Cenário Pessimista
              </span>
              <span className="text-xs font-mono font-bold text-rose-700">
                {simulador?.pessimista.probabilidade_afastamento_12m ?? 24.0}% prob.
              </span>
            </div>
            <h4 className="text-xs font-bold text-rose-950">
              {simulador?.pessimista.descricao || 'Negligência postural e sobrecarga contínua'}
            </h4>
            <p className="text-[11px] text-gray-600">
              Risco de afastamento médico acumulado: <strong>{simulador?.pessimista.estimativa_dias_afastamento ?? 14} dias</strong>.
            </p>
            <ul className="text-[11px] text-rose-900 space-y-1 pt-1 border-t border-rose-100">
              {(simulador?.pessimista.complicacoes_potenciais || [
                'Agravamento de lombalgia mecânica',
                'Demanda de fisioterapia corretiva intensiva',
              ]).map((c, i) => (
                <li key={i} className="flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-rose-600 shrink-0" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Acompanhamento da Sua Saúde (Evolução Longitudinal) */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-3 border-b border-gray-100 gap-2">
          <div>
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <Activity className="w-4 h-4 text-emerald-600" />
              Acompanhamento da Sua Saúde (Evolução Longitudinal)
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Entenda como a sua saúde está evoluindo com base no histórico de exames periódicos e registros médicos.
            </p>
          </div>
          <div>{getTrajetoriaColaboradorBadge(saude?.trajetoria_clinica)}</div>
        </div>

        {/* Perguntas e Respostas da Trajetória */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3.5 text-xs">
          <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 space-y-1">
            <span className="font-bold text-slate-900 block text-[11px] uppercase tracking-wider">
              Esse achado já existia?
            </span>
            <p className="text-slate-600 leading-relaxed text-[11px]">
              {saude?.perguntas_investigativas?.ja_existia ||
                'Os registros apontam que o histórico foi mapeado no seu admissional ou exames anteriores.'}
            </p>
          </div>

          <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 space-y-1">
            <span className="font-bold text-slate-900 block text-[11px] uppercase tracking-wider">
              Está melhorando ou piorando?
            </span>
            <p className="text-slate-600 leading-relaxed text-[11px]">
              {saude?.perguntas_investigativas?.piorando_ou_melhorando ||
                'Seu quadro clínico está em estabilidade, sem piora acelerada detectada nos periódicos.'}
            </p>
          </div>

          <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 space-y-1">
            <span className="font-bold text-slate-900 block text-[11px] uppercase tracking-wider">
              É uma alteração pontual?
            </span>
            <p className="text-slate-600 leading-relaxed text-[11px]">
              {saude?.perguntas_investigativas?.isolada_ou_recorrente ||
                'Trata-se de alteração circunstancial que melhora com pausas de descanso e autocuidado ativo.'}
            </p>
          </div>

          <div className="bg-emerald-50/70 p-3.5 rounded-xl border border-emerald-200 space-y-1">
            <span className="font-bold text-emerald-950 block text-[11px] uppercase tracking-wider">
              O que você pode fazer?
            </span>
            <p className="text-emerald-900 leading-relaxed text-[11px]">
              {saude?.perguntas_investigativas?.investigar_reduzir_incerteza?.[0] ||
                'Mantenha boa hidratação, pratique pausas ativas na jornada e participe do próximo periódico anual.'}
            </p>
          </div>
        </div>
      </div>

      {/* Exames Complementares & Rotina Preventiva do PCMSO */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-gray-100">
          <div>
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <Stethoscope className="w-4 h-4 text-indigo-600" />
              Exames Complementares & Rotina Preventiva do PCMSO
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Acompanhamentos médicos e avaliações periódicas planejadas pelo Médico do Trabalho para zelar pela sua saúde.
            </p>
          </div>
          <span className="text-xs font-bold text-indigo-800 bg-indigo-50 px-2.5 py-1 rounded-full border border-indigo-200">
            Prevenção Ocupacional
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(recomendacoes?.exames_complementares_sugeridos || [
            {
              exame_nome: 'Aferição de Pressão Arterial e Glicemia Capilar no Periódico',
              finalidade: 'Acompanhamento preventivo regular de saúde cardiovascular',
              prioridade: 'ROTINA' as const,
              justificativa_clinica: 'Manutenção de autocuidado e controle regular no ciclo do PCMSO',
            },
            {
              exame_nome: 'Avaliação Ergonômica Funcional e Postural',
              finalidade: 'Prevenção de sobrecarga lombar e desconfortos musculares',
              prioridade: 'ROTINA' as const,
              justificativa_clinica: 'Orientações posturais alinhadas às tarefas do seu posto de trabalho',
            },
          ]).map((item, idx) => (
            <div
              key={idx}
              className="p-4 rounded-xl border border-indigo-200/80 bg-gradient-to-br from-indigo-50/30 via-white to-white space-y-2 shadow-2xs"
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-indigo-800 bg-indigo-100 px-2 py-0.5 rounded uppercase">
                  {item.finalidade}
                </span>
                <span
                  className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase border ${
                    item.prioridade === 'ALTA'
                      ? 'bg-rose-100 text-rose-800 border-rose-200'
                      : item.prioridade === 'MEDIA'
                      ? 'bg-amber-100 text-amber-800 border-amber-200'
                      : 'bg-emerald-100 text-emerald-800 border-emerald-200'
                  }`}
                >
                  {item.prioridade}
                </span>
              </div>
              <h4 className="text-xs font-bold text-gray-900 flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                {item.exame_nome}
              </h4>
              <p className="text-xs text-gray-600">
                <strong>Justificativa PCMSO:</strong> {item.justificativa_clinica}
              </p>
            </div>
          ))}
        </div>

        <p className="text-[11px] text-gray-500 pt-1 italic">
          *As solicitações e direcionamentos de exames são avaliados e acompanhados pelo Médico Coordenador do PCMSO.
        </p>
      </div>

      {/* Grid: Cuidados com o Corpo & Linha do Tempo */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Cuidados com o seu Corpo & Ergonomia */}
        <div className="lg:col-span-7 bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <Heart className="w-5 h-5 text-rose-500" />
              Cuidados com o seu Corpo (Vigilância Preventiva)
            </h3>
            <span className="text-xs text-gray-400">Recomendações Ocupacionais</span>
          </div>

          <div className="space-y-3">
            {(saude?.achados_simplificados || []).map((item, idx) => (
              <div
                key={idx}
                className={`p-3.5 rounded-xl border transition-all ${getCorSistema(
                  item.sistema,
                )}`}
              >
                <div className="flex items-center gap-2 font-bold text-xs mb-1">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>{item.titulo}</span>
                </div>
                <p className="text-xs text-gray-700 leading-relaxed pl-6">
                  {item.orientacao}
                </p>
              </div>
            ))}
          </div>

          {/* Hábitos e Cuidados Posturais */}
          <div className="pt-3 border-t border-gray-100 space-y-3">
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
              <Info className="w-3.5 h-3.5 text-indigo-500" />
              Cuidados Posturais & Ergonomia Recomendados
            </h4>
            <ul className="space-y-1.5 text-xs text-gray-600">
              {(recomendacoes?.cuidados_posturais_ergonomicos || saude?.orientacoes_bem_estar || [
                'Manter postura alinhada e apoio nos pés durante toda a jornada.',
                'Fazer pausas ativas a cada 90 minutos para alongamento.',
              ]).map((c, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-emerald-500 font-bold">•</span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Linha do Tempo de Atendimentos */}
        <div className="lg:col-span-5 bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-indigo-600" />
              Sua Linha do Tempo
            </h3>
            <span className="text-xs text-gray-400">Histórico de Registros</span>
          </div>

          <div className="relative border-l-2 border-emerald-100 ml-3 pl-4 space-y-4 max-h-[380px] overflow-y-auto pr-1">
            {isLoading ? (
              <p className="text-xs text-gray-400">Carregando histórico...</p>
            ) : (saude?.timeline || []).length === 0 ? (
              <p className="text-xs text-gray-500">
                Nenhum evento registrado ainda. Quando você realizar exames ou enviar atestados, eles aparecerão aqui.
              </p>
            ) : (
              saude?.timeline.map((ev) => (
                <div key={ev.id} className="relative group text-xs">
                  <div
                    className={`absolute -left-[23px] top-1 w-3.5 h-3.5 rounded-full border-2 border-white shadow-sm ${
                      ev.tipo_evento === 'ATESTADO_MEDICO' ? 'bg-amber-500' : 'bg-emerald-500'
                    }`}
                  />
                  <div className="bg-gray-50 hover:bg-gray-100/80 rounded-lg p-2.5 border border-gray-200 transition-colors">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-gray-900">{ev.titulo}</span>
                      <span className="text-[10px] text-gray-400">
                        {new Date(ev.data_evento).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                    {ev.subtitulo && (
                      <p className="text-gray-500 text-[11px] mt-0.5">{ev.subtitulo}</p>
                    )}
                    <div className="flex items-center gap-2 mt-1.5">
                      <span
                        className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                          ev.tipo_evento === 'ATESTADO_MEDICO'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-emerald-100 text-emerald-800'
                        }`}
                      >
                        {ev.parecer_ou_status}
                      </span>
                      {ev.dias_afastamento > 0 && (
                        <span className="text-rose-600 font-semibold text-[10px]">
                          {ev.dias_afastamento} dias de repouso
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Modal de Upload de Atestado */}
      <UploadAtestadoModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        empresaId={empresaId}
        colaboradorIdPreSelecionado={colaboradorId}
        onSuccess={() => {
          setIsUploadModalOpen(false);
          refetch();
          showToast('success', 'Atestado enviado com sucesso! O documento será analisado pelo RH.');
        }}
        toast={{
          success: (msg) => showToast('success', msg),
          error: (msg) => showToast('error', msg),
        }}
      />
    </div>
  );
}
